import { Link, NavLink, useNavigate } from "react-router-dom";
import { Clapperboard, Home, PenSquare, History, Link2, Gem, LogOut, Coins, Car } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { ThemeToggle } from "@/components/ThemeToggle";

const NAV = [
  { to: "/", label: "Home", icon: Home, end: true },
  { to: "/editor", label: "Editor", icon: Clapperboard },
  { to: "/car-edits", label: "Car Edits", icon: Car },
  { to: "/compose", label: "Compose", icon: PenSquare },
  { to: "/history", label: "History", icon: History },
  { to: "/connections", label: "Connections", icon: Link2 },
  { to: "/membership", label: "Membership", icon: Gem },
];

export const Logo = () => (
  <Link to="/" className="flex items-center gap-2.5" data-testid="logo-link">
    <span className="h-8 w-8 rounded-lg bg-primary text-primary-foreground grid place-items-center" style={{ boxShadow: "0 0 18px 0 var(--glow)" }}><Clapperboard size={16} strokeWidth={2.5} /></span>
    <span className="font-display text-xl tracking-wide">Reel<span className="text-primary">Flo</span><span className="text-muted-foreground text-sm align-top">.io</span></span>
  </Link>
);

export const Layout = ({ children }) => {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const linkCls = ({ isActive }) =>
    `flex items-center gap-2 px-3 py-2 rounded-xl text-sm font-medium transition-colors ${isActive ? "bg-primary/12 text-primary" : "text-muted-foreground hover:text-foreground hover:bg-muted"}`;
  return (
    <div className="min-h-screen grain relative overflow-x-hidden">
      <div className="glow-blob w-[520px] h-[520px] -top-40 -left-40 opacity-70" />
      <div className="glow-blob w-[420px] h-[420px] top-1/3 -right-52 opacity-50" />
      <header className="fixed top-0 inset-x-0 z-50 glass">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 h-16 flex items-center justify-between gap-4">
          <Logo />
          <nav className="hidden lg:flex items-center gap-1" data-testid="desktop-nav">
            {NAV.map((n) => (
              <NavLink key={n.to} to={n.to} end={n.end} className={linkCls} data-testid={`nav-${n.label.toLowerCase()}`}>
                <n.icon size={15} /> {n.label}
              </NavLink>
            ))}
          </nav>
          <div className="flex items-center gap-2 sm:gap-3">
            {user && (
              <button onClick={() => navigate("/membership")} className="hidden sm:flex items-center gap-1.5 rounded-xl border border-border bg-card px-3 h-9 text-sm font-mono hover:border-primary/60 transition-colors" data-testid="credits-balance">
                <Coins size={14} className="text-primary" /> {user.credits}
              </button>
            )}
            <ThemeToggle />
            {user && (
              <div className="flex items-center gap-2">
                {user.picture ? <img src={user.picture} alt="" className="h-9 w-9 rounded-full border border-border" data-testid="user-avatar" /> : null}
                <button onClick={async () => { await logout(); navigate("/"); }} className="h-9 w-9 rounded-xl border border-border bg-card grid place-items-center text-muted-foreground hover:text-destructive transition-colors" data-testid="logout-button" aria-label="Log out">
                  <LogOut size={15} />
                </button>
              </div>
            )}
          </div>
        </div>
      </header>
      <main className="relative z-10 pt-24 pb-28 lg:pb-16 px-4 sm:px-6 max-w-7xl mx-auto">{children}</main>
      {user && (
        <nav className="lg:hidden fixed bottom-0 inset-x-0 z-50 glass border-t border-border/50 grid grid-cols-7" data-testid="mobile-nav">
          {NAV.map((n) => (
            <NavLink key={n.to} to={n.to} end={n.end} className={({ isActive }) => `flex flex-col items-center gap-1 py-2.5 text-[10px] font-medium ${isActive ? "text-primary" : "text-muted-foreground"}`} data-testid={`mobile-nav-${n.label.toLowerCase()}`}>
              <n.icon size={18} /> {n.label}
            </NavLink>
          ))}
        </nav>
      )}
    </div>
  );
};
